import React, { useCallback, useMemo, useState } from 'react';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ProformaAmountRejectedError,
  strapiClearWorkOrderProformaAmount,
  strapiListInvoiceGridRows,
  strapiListInvoicePayments,
  strapiRunInvoiceWorkOrderAction,
  strapiSaveWorkOrderProformaAmount,
  type InvoiceGridSort,
  type InvoiceStatus,
  type InvoiceTestingStatus,
  type InvoiceWorkflowAction,
  type StrapiInvoiceGridRow,
} from '../../../../api/strapiCrm';
import { useAuth } from '../../../../auth/AuthContext';
import {
  DataGridNumberedPagination,
  DATA_GRID_DEFAULT_PAGE_SIZE,
  DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS,
} from '../../../../components/DataGridNumberedPagination';
import { queryKeys } from '../../../../query/queryKeys';
import { CollapsibleListFilters } from '../../../../ui/CollapsibleListFilters';
import { InvoicePdfPreviewDialog } from '../../InvoicePdfPreviewDialog';
import {
  formatInvoiceGrandTotal,
  formatInvoicePaidAmount,
  formatInvoiceRequestDate,
  INVOICE_STATUS_LABEL,
  TESTING_STATUS_LABEL,
} from '../../invoiceGridLabels';
import {
  INVOICE_WORKFLOW_ACTION_LABEL,
  INVOICE_WORKFLOW_CONFIRM,
} from '../../invoiceWorkflowLabels';

const LIST_SORT: InvoiceGridSort = 'quotation_request_date:desc';

const INVOICE_CHIP_COLOR: Record<InvoiceStatus, 'default' | 'warning' | 'success'> = {
  created: 'default',
  down_payment: 'warning',
  full_payment: 'success',
};

const TESTING_CHIP_COLOR: Record<
  InvoiceTestingStatus,
  'info' | 'warning' | 'default' | 'secondary' | 'success' | 'error'
> = {
  pengujian: 'info',
  verifikasi: 'warning',
  draft_lhu: 'default',
  verifikasi_lhu: 'secondary',
  cetak: 'success',
  revisi_lhu: 'error',
  lhu_rilis: 'success',
};

type ConfirmAction = Extract<
  InvoiceWorkflowAction,
  'approve_gl' | 'reject_gl' | 'down_payment' | 'full_payment'
>;

type ProformaPromptState = {
  row: StrapiInvoiceGridRow;
  amount: string;
  error: string | null;
  maxAmount: number | null;
};

function proformaAmountLimit(prompt: ProformaPromptState): number {
  if (prompt.maxAmount != null && prompt.maxAmount > 0) return prompt.maxAmount;
  if (prompt.row.pdf_grand_total > 0) return prompt.row.pdf_grand_total;
  return prompt.row.grand_total;
}

function invoiceRemaining(row: StrapiInvoiceGridRow): number {
  return Math.max(0, Math.round(row.grand_total) - Math.round(row.paid_amount));
}

function formatPaymentAt(value: string): string {
  if (!value.trim()) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('id-ID');
}

function InvoiceActionIcon({
  title,
  ariaLabel,
  testId,
  disabled,
  color,
  onClick,
  children,
}: {
  title: string;
  ariaLabel: string;
  testId: string;
  disabled?: boolean;
  color?: 'default' | 'success' | 'error' | 'primary';
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const button = (
    <IconButton
      size="small"
      color={color}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </IconButton>
  );
  return <Tooltip title={title}>{disabled ? <span>{button}</span> : button}</Tooltip>;
}

export function InvoiceListTabScreen() {
  const { jwt } = useAuth();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ action: ConfirmAction; row: StrapiInvoiceGridRow } | null>(null);
  const [dpModal, setDpModal] = useState<{ row: StrapiInvoiceGridRow; amount: string; note: string } | null>(
    null,
  );
  const [paymentHistory, setPaymentHistory] = useState<StrapiInvoiceGridRow | null>(null);
  const [invoicePdfPreview, setInvoicePdfPreview] = useState<StrapiInvoiceGridRow | null>(null);
  const [proformaPreview, setProformaPreview] = useState<StrapiInvoiceGridRow | null>(null);
  const [proformaPrompt, setProformaPrompt] = useState<ProformaPromptState | null>(null);
  const [proformaSaving, setProformaSaving] = useState(false);
  const [proformaResetting, setProformaResetting] = useState(false);

  const rowsQ = useQuery({
    queryKey: queryKeys.invoiceGridRows(jwt, LIST_SORT),
    queryFn: () => strapiListInvoiceGridRows(jwt!, LIST_SORT),
    enabled: Boolean(jwt),
  });

  const historyQ = useQuery({
    queryKey: queryKeys.invoicePayments(jwt, paymentHistory?.work_order_document_id ?? null),
    queryFn: () => strapiListInvoicePayments(jwt!, paymentHistory!.work_order_document_id),
    enabled: Boolean(jwt && paymentHistory),
  });

  const actionMut = useMutation({
    mutationFn: ({
      workOrderDocumentId,
      action,
      paidAmount,
      paymentNote,
    }: {
      workOrderDocumentId: string;
      action: ConfirmAction;
      paidAmount?: number;
      paymentNote?: string;
    }) =>
      strapiRunInvoiceWorkOrderAction(
        jwt!,
        workOrderDocumentId,
        action,
        paidAmount != null || paymentNote
          ? {
              ...(paidAmount != null ? { paid_amount: paidAmount } : {}),
              ...(paymentNote ? { payment_note: paymentNote } : {}),
            }
          : undefined,
      ),
    onSuccess: async (_row, vars) => {
      setActionError(null);
      setActionSuccess(`${INVOICE_WORKFLOW_ACTION_LABEL[vars.action]} berhasil.`);
      setConfirm(null);
      setDpModal(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.invoiceGridRows(jwt) });
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.strapi, 'invoicePayments'] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.laboratoryResultGridRowsPrefix(jwt) });
    },
    onError: (e) => {
      setActionSuccess(null);
      setActionError(e instanceof Error ? e.message : 'Gagal menjalankan aksi invoice.');
    },
  });

  const requestAction = useCallback((action: ConfirmAction, row: StrapiInvoiceGridRow) => {
    setActionError(null);
    setActionSuccess(null);
    if (action === 'down_payment') {
      setDpModal({ row, amount: '', note: '' });
      return;
    }
    if (INVOICE_WORKFLOW_CONFIRM[action]) {
      setConfirm({ action, row });
      return;
    }
    actionMut.mutate({ workOrderDocumentId: row.work_order_document_id, action });
  }, [actionMut]);

  const rows = rowsQ.data ?? [];

  const columns: GridColDef<StrapiInvoiceGridRow>[] = useMemo(
    () => [
      {
        field: 'company_name',
        headerName: 'Nama perusahaan',
        minWidth: 180,
        flex: 1.1,
        valueGetter: (_, row) => row.company_name || '—',
      },
      {
        field: 'customer_npwp',
        headerName: 'NPWP',
        minWidth: 180,
        flex: 0.9,
        valueGetter: (_, row) => row.customer_npwp || '—',
      },
      {
        field: 'is_ppn',
        headerName: 'PPN',
        minWidth: 110,
        width: 120,
        flex: 0,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_, row) => row.is_ppn,
        renderCell: ({ row }) => {
          const yes = row.is_ppn;
          return (
            <Tooltip title={yes ? 'Yes' : 'No'}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                }}
                data-testid={`invoice-ppn-${row.work_order_document_id}`}
                aria-label={yes ? 'PPN yes' : 'PPN no'}
              >
                {yes ? (
                  <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: 'error.main' }} />
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'quotation_number',
        headerName: 'No quotation',
        minWidth: 140,
        flex: 0.8,
        valueGetter: (_, row) => row.quotation_number || '—',
      },
      {
        field: 'job_number',
        headerName: 'No job number',
        minWidth: 120,
        flex: 0.7,
        valueGetter: (_, row) => row.job_number || '—',
      },
      {
        field: 'quotation_request_date',
        headerName: 'Tanggal permintaan quotation',
        minWidth: 190,
        flex: 0.8,
        valueGetter: (_, row) => row.quotation_request_date || '',
        valueFormatter: (value) => formatInvoiceRequestDate(typeof value === 'string' ? value : null),
      },
      {
        field: 'grand_total',
        headerName: 'Grand total',
        minWidth: 140,
        flex: 0.7,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_, row) => formatInvoiceGrandTotal(row.grand_total),
      },
      {
        field: 'paid_amount',
        headerName: 'Paid Amount',
        minWidth: 140,
        flex: 0.7,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_, row) => formatInvoicePaidAmount(row.paid_amount),
      },
      {
        field: 'invoice_status',
        headerName: 'Status invoice',
        minWidth: 150,
        flex: 0.7,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={INVOICE_STATUS_LABEL[row.invoice_status] ?? row.invoice_status}
            color={INVOICE_CHIP_COLOR[row.invoice_status] ?? 'default'}
          />
        ),
      },
      {
        field: 'testing_status',
        headerName: 'Status pengujian',
        minWidth: 160,
        flex: 0.8,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={TESTING_STATUS_LABEL[row.testing_status] ?? row.testing_status}
            color={TESTING_CHIP_COLOR[row.testing_status] ?? 'default'}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Aksi',
        minWidth: 284,
        width: 296,
        flex: 0,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const pending = actionMut.isPending;
          const woKey = row.work_order_document_id;
          return (
            <Box
              sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {row.actions.approve_gl ? (
                <InvoiceActionIcon
                  title={INVOICE_WORKFLOW_ACTION_LABEL.approve_gl}
                  ariaLabel={`${INVOICE_WORKFLOW_ACTION_LABEL.approve_gl} ${woKey}`}
                  testId={`invoice-action-approve-gl-${woKey}`}
                  color="success"
                  disabled={pending}
                  onClick={() => requestAction('approve_gl', row)}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                </InvoiceActionIcon>
              ) : null}
              {row.actions.reject_gl ? (
                <InvoiceActionIcon
                  title={INVOICE_WORKFLOW_ACTION_LABEL.reject_gl}
                  ariaLabel={`${INVOICE_WORKFLOW_ACTION_LABEL.reject_gl} ${woKey}`}
                  testId={`invoice-action-reject-gl-${woKey}`}
                  color="error"
                  disabled={pending}
                  onClick={() => requestAction('reject_gl', row)}
                >
                  <CancelOutlinedIcon fontSize="small" />
                </InvoiceActionIcon>
              ) : null}
              {row.actions.down_payment ? (
                <InvoiceActionIcon
                  title={INVOICE_WORKFLOW_ACTION_LABEL.down_payment}
                  ariaLabel={`${INVOICE_WORKFLOW_ACTION_LABEL.down_payment} ${woKey}`}
                  testId={`invoice-action-down-payment-${woKey}`}
                  disabled={pending}
                  onClick={() => requestAction('down_payment', row)}
                >
                  <RequestQuoteOutlinedIcon fontSize="small" />
                </InvoiceActionIcon>
              ) : null}
              {row.actions.full_payment ? (
                <InvoiceActionIcon
                  title={INVOICE_WORKFLOW_ACTION_LABEL.full_payment}
                  ariaLabel={`${INVOICE_WORKFLOW_ACTION_LABEL.full_payment} ${woKey}`}
                  testId={`invoice-action-full-payment-${woKey}`}
                  color="success"
                  disabled={pending}
                  onClick={() => requestAction('full_payment', row)}
                >
                  <PaidOutlinedIcon fontSize="small" />
                </InvoiceActionIcon>
              ) : null}
              <InvoiceActionIcon
                title="Riwayat pembayaran"
                ariaLabel={`Riwayat pembayaran ${woKey}`}
                testId={`invoice-action-payment-history-${woKey}`}
                disabled={pending}
                onClick={() => setPaymentHistory(row)}
              >
                <HistoryOutlinedIcon fontSize="small" />
              </InvoiceActionIcon>
              {row.actions.preview_proforma ? (
                <InvoiceActionIcon
                  title="Cetak proforma invoice"
                  ariaLabel={`Cetak proforma invoice ${woKey}`}
                  testId={`invoice-action-proforma-${woKey}`}
                  color="primary"
                  disabled={pending || proformaSaving || proformaResetting}
                  onClick={() => {
                    if (row.proforma_amount != null && row.proforma_amount > 0) {
                      const limit = row.pdf_grand_total > 0 ? row.pdf_grand_total : row.grand_total;
                      if (row.proforma_amount > limit) {
                        setProformaPrompt({
                          row,
                          amount: String(Math.round(row.proforma_amount)),
                          error: 'Jumlah bayar melebihi grand total.',
                          maxAmount: null,
                        });
                        return;
                      }
                      setProformaPreview(row);
                      return;
                    }
                    setProformaPrompt({ row, amount: '', error: null, maxAmount: null });
                  }}
                >
                  <ReceiptLongOutlinedIcon fontSize="small" />
                </InvoiceActionIcon>
              ) : null}
              {row.actions.preview_invoice ? (
                <InvoiceActionIcon
                  title={INVOICE_WORKFLOW_ACTION_LABEL.preview_invoice}
                  ariaLabel={`${INVOICE_WORKFLOW_ACTION_LABEL.preview_invoice} ${woKey}`}
                  testId={`invoice-action-preview-invoice-${woKey}`}
                  color="primary"
                  disabled={pending}
                  onClick={() => setInvoicePdfPreview(row)}
                >
                  <PictureAsPdfOutlinedIcon fontSize="small" />
                </InvoiceActionIcon>
              ) : null}
            </Box>
          );
        },
      },
    ],
    [actionMut.isPending, proformaResetting, proformaSaving, requestAction],
  );

  const gridRows = useMemo(() => rows.map((r) => ({ ...r, id: r.row_key })), [rows]);

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CollapsibleListFilters
        actions={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={rowsQ.isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={!jwt || rowsQ.isFetching}
            onClick={() => void rowsQ.refetch()}
            data-testid="invoices-refresh-button"
          >
            Muat ulang
          </Button>
        }
      />

      {rowsQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {rowsQ.error instanceof Error ? rowsQ.error.message : 'Gagal memuat daftar invoice.'}
        </Alert>
      ) : null}

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {actionSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      ) : null}

      {!rowsQ.isPending && !rowsQ.isFetching && rows.length === 0 && !rowsQ.isError ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Belum ada work order yang sample-nya sudah diterima kantor. Invoice muncul setelah admin
          penerima sample menandai Sample received.
        </Alert>
      ) : null}

      <DataGrid
        rows={gridRows}
        columns={columns}
        loading={rowsQ.isPending || rowsQ.isFetching}
        disableRowSelectionOnClick
        pageSizeOptions={DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS}
        initialState={{
          pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } },
          sorting: { sortModel: [{ field: 'quotation_request_date', sort: 'desc' }] },
        }}
        slots={{ pagination: DataGridNumberedPagination }}
        sx={{
          flex: 1,
          minHeight: 360,
          '& .MuiDataGrid-cell': { alignItems: 'center' },
        }}
      />

      <Dialog
        open={Boolean(confirm)}
        onClose={() => (actionMut.isPending ? undefined : setConfirm(null))}
      >
        <DialogTitle>{confirm ? INVOICE_WORKFLOW_ACTION_LABEL[confirm.action] : 'Konfirmasi'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm ? INVOICE_WORKFLOW_CONFIRM[confirm.action] : ''}
          </Typography>
          {confirm?.action === 'full_payment' ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sisa tagihan: {formatInvoiceGrandTotal(invoiceRemaining(confirm.row))}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={actionMut.isPending}>
            Batal
          </Button>
          <Button
            variant="contained"
            color={confirm?.action === 'reject_gl' ? 'error' : 'primary'}
            disabled={!confirm || actionMut.isPending}
            onClick={() => {
              if (!confirm) return;
              actionMut.mutate({
                workOrderDocumentId: confirm.row.work_order_document_id,
                action: confirm.action,
              });
            }}
            data-testid="invoice-action-confirm"
          >
            {actionMut.isPending ? 'Memproses…' : 'Ya, lanjutkan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(dpModal)}
        onClose={() => (actionMut.isPending ? undefined : setDpModal(null))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{INVOICE_WORKFLOW_ACTION_LABEL.down_payment}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {dpModal
              ? [dpModal.row.job_number, dpModal.row.company_name].filter(Boolean).join(' · ')
              : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Grand total: {dpModal ? formatInvoiceGrandTotal(dpModal.row.grand_total) : '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sudah dibayar: {dpModal ? formatInvoicePaidAmount(dpModal.row.paid_amount) : '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sisa: {dpModal ? formatInvoiceGrandTotal(invoiceRemaining(dpModal.row)) : '—'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Jumlah bayar"
            placeholder="Contoh: 5000000"
            value={dpModal?.amount ?? ''}
            onChange={(e) => {
              const next = e.target.value.replace(/[^\d]/g, '');
              setDpModal((prev) => (prev ? { ...prev, amount: next } : prev));
            }}
            error={Boolean(
              dpModal &&
                dpModal.amount &&
                Number(dpModal.amount) > invoiceRemaining(dpModal.row),
            )}
            helperText={
              dpModal && dpModal.amount && Number(dpModal.amount) > invoiceRemaining(dpModal.row)
                ? 'Tidak boleh melebihi sisa tagihan.'
                : dpModal?.amount
                  ? formatInvoicePaidAmount(Number(dpModal.amount))
                  : 'Masukkan jumlah uang yang sudah diterima.'
            }
            inputProps={{ inputMode: 'numeric', 'data-testid': 'invoice-dp-paid-amount' }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Catatan"
            placeholder="Opsional"
            value={dpModal?.note ?? ''}
            onChange={(e) => {
              const next = e.target.value;
              setDpModal((prev) => (prev ? { ...prev, note: next } : prev));
            }}
            inputProps={{ 'data-testid': 'invoice-dp-note' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDpModal(null)} disabled={actionMut.isPending}>
            Batal
          </Button>
          <Button
            variant="contained"
            disabled={
              !dpModal ||
              actionMut.isPending ||
              !dpModal.amount ||
              Number(dpModal.amount) <= 0 ||
              Number(dpModal.amount) > invoiceRemaining(dpModal.row)
            }
            onClick={() => {
              if (!dpModal) return;
              const paidAmount = Number(dpModal.amount);
              if (!Number.isFinite(paidAmount) || paidAmount <= 0) return;
              if (paidAmount > invoiceRemaining(dpModal.row)) return;
              actionMut.mutate({
                workOrderDocumentId: dpModal.row.work_order_document_id,
                action: 'down_payment',
                paidAmount,
                paymentNote: dpModal.note.trim() || undefined,
              });
            }}
            data-testid="invoice-dp-save"
          >
            {actionMut.isPending ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(paymentHistory)}
        onClose={() => setPaymentHistory(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Riwayat pembayaran</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {paymentHistory
              ? [
                  paymentHistory.quotation_number,
                  paymentHistory.job_number,
                  paymentHistory.company_name,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : ''}
          </Typography>
          {historyQ.isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : null}
          {historyQ.isError ? (
            <Alert severity="error">
              {historyQ.error instanceof Error ? historyQ.error.message : 'Gagal memuat riwayat pembayaran.'}
            </Alert>
          ) : null}
          {historyQ.data && !historyQ.isPending ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Grand total: {formatInvoiceGrandTotal(historyQ.data.grand_total)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sudah dibayar: {formatInvoicePaidAmount(historyQ.data.paid_amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sisa: {formatInvoiceGrandTotal(historyQ.data.remaining_amount)}
              </Typography>
              {historyQ.data.payments.length === 0 ? (
                <Typography variant="body2">Belum ada pembayaran.</Typography>
              ) : (
                <Table size="small" data-testid="invoice-payment-history">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tanggal</TableCell>
                      <TableCell align="right">Nominal</TableCell>
                      <TableCell>Catatan</TableCell>
                      <TableCell>No job</TableCell>
                      <TableCell>Dicatat oleh</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyQ.data.payments.map((payment, index) => (
                      <TableRow key={payment.document_id || `${payment.paid_at}-${index}`}>
                        <TableCell>{formatPaymentAt(payment.paid_at)}</TableCell>
                        <TableCell align="right">{formatInvoiceGrandTotal(payment.amount)}</TableCell>
                        <TableCell>{payment.note || '—'}</TableCell>
                        <TableCell>{payment.job_number || '—'}</TableCell>
                        <TableCell>{payment.recorded_by_name || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentHistory(null)}>Tutup</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(proformaPrompt)}
        onClose={() => (proformaSaving ? undefined : setProformaPrompt(null))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cetak proforma invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {proformaPrompt
              ? [proformaPrompt.row.job_number, proformaPrompt.row.company_name].filter(Boolean).join(' · ')
              : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Grand total: {proformaPrompt ? formatInvoiceGrandTotal(proformaAmountLimit(proformaPrompt)) : '—'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Jumlah bayar"
            placeholder="Contoh: 5000000"
            value={proformaPrompt?.amount ?? ''}
            onChange={(e) => {
              const next = e.target.value.replace(/[^\d]/g, '');
              setProformaPrompt((prev) => (prev ? { ...prev, amount: next, error: null } : prev));
            }}
            error={Boolean(
              proformaPrompt?.error ||
                (proformaPrompt?.amount &&
                  Number(proformaPrompt.amount) > proformaAmountLimit(proformaPrompt)),
            )}
            helperText={
              proformaPrompt?.error
                ? proformaPrompt.error
                : proformaPrompt?.amount &&
                    Number(proformaPrompt.amount) > proformaAmountLimit(proformaPrompt)
                  ? 'Jumlah bayar melebihi grand total.'
                  : proformaPrompt?.amount
                    ? formatInvoicePaidAmount(Number(proformaPrompt.amount))
                    : 'Jumlah yang akan dibayarkan customer.'
            }
            inputProps={{ inputMode: 'numeric', 'data-testid': 'invoice-proforma-amount' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProformaPrompt(null)} disabled={proformaSaving}>
            Batal
          </Button>
          <Button
            variant="contained"
            disabled={
              !proformaPrompt ||
              !jwt ||
              proformaSaving ||
              !proformaPrompt.amount ||
              Number(proformaPrompt.amount) <= 0 ||
              Number(proformaPrompt.amount) > proformaAmountLimit(proformaPrompt)
            }
            onClick={() => {
              if (!proformaPrompt || !jwt) return;
              const paidAmount = Number(proformaPrompt.amount);
              if (!Number.isFinite(paidAmount) || paidAmount <= 0) return;
              if (paidAmount > proformaAmountLimit(proformaPrompt)) return;
              const row = proformaPrompt.row;
              setProformaSaving(true);
              setProformaPrompt((prev) => (prev ? { ...prev, error: null } : prev));
              void strapiSaveWorkOrderProformaAmount(jwt, row.work_order_document_id, paidAmount)
                .then(async (saved) => {
                  queryClient.setQueryData<StrapiInvoiceGridRow[]>(
                    queryKeys.invoiceGridRows(jwt, LIST_SORT),
                    (prev) =>
                      prev?.map((item) =>
                        item.work_order_document_id === row.work_order_document_id
                          ? { ...item, proforma_amount: saved }
                          : item,
                      ),
                  );
                  setProformaPrompt(null);
                  setProformaPreview({ ...row, proforma_amount: saved });
                  await queryClient.invalidateQueries({ queryKey: queryKeys.invoiceGridRows(jwt) });
                })
                .catch((e) => {
                  const message = e instanceof Error ? e.message : 'Gagal menyimpan jumlah bayar proforma.';
                  const grandTotal = e instanceof ProformaAmountRejectedError ? e.grandTotal : null;
                  setProformaPrompt((prev) =>
                    prev
                      ? {
                          ...prev,
                          error: message,
                          maxAmount: grandTotal != null && grandTotal > 0 ? grandTotal : prev.maxAmount,
                        }
                      : prev,
                  );
                })
                .finally(() => setProformaSaving(false));
            }}
            data-testid="invoice-proforma-save"
          >
            {proformaSaving ? 'Menyimpan…' : 'Cetak'}
          </Button>
        </DialogActions>
      </Dialog>

      <InvoicePdfPreviewDialog
        open={Boolean(invoicePdfPreview)}
        workOrderDocumentId={invoicePdfPreview?.work_order_document_id ?? null}
        workOrderLabel={
          invoicePdfPreview
            ? invoicePdfPreview.job_number ||
              invoicePdfPreview.quotation_number ||
              invoicePdfPreview.company_name
            : null
        }
        jwt={jwt}
        onClose={() => setInvoicePdfPreview(null)}
      />

      <InvoicePdfPreviewDialog
        open={Boolean(proformaPreview)}
        variant="proforma"
        workOrderDocumentId={proformaPreview?.work_order_document_id ?? null}
        workOrderLabel={
          proformaPreview
            ? proformaPreview.job_number ||
              proformaPreview.quotation_number ||
              proformaPreview.company_name
            : null
        }
        jwt={jwt}
        resettingProformaAmount={proformaResetting}
        onLoadError={(message, grandTotal) => {
          if (!proformaPreview || !message.includes('melebihi grand total')) return false;
          const row = proformaPreview;
          setProformaPreview(null);
          setProformaPrompt({
            row,
            amount: row.proforma_amount != null ? String(Math.round(row.proforma_amount)) : '',
            error: message,
            maxAmount: grandTotal != null && grandTotal > 0 ? grandTotal : null,
          });
          return true;
        }}
        onResetProformaAmount={() => {
          if (!proformaPreview || !jwt || proformaResetting) return;
          const row = proformaPreview;
          setProformaResetting(true);
          setActionError(null);
          void strapiClearWorkOrderProformaAmount(jwt, row.work_order_document_id)
            .then(async () => {
              queryClient.setQueryData<StrapiInvoiceGridRow[]>(
                queryKeys.invoiceGridRows(jwt, LIST_SORT),
                (prev) =>
                  prev?.map((item) =>
                    item.work_order_document_id === row.work_order_document_id
                      ? { ...item, proforma_amount: null }
                      : item,
                  ),
              );
              setProformaPreview(null);
              setProformaPrompt({
                row: { ...row, proforma_amount: null },
                amount: '',
                error: null,
                maxAmount: null,
              });
              await queryClient.invalidateQueries({ queryKey: queryKeys.invoiceGridRows(jwt) });
            })
            .catch((e) => {
              setActionError(e instanceof Error ? e.message : 'Gagal mereset jumlah bayar proforma.');
            })
            .finally(() => setProformaResetting(false));
        }}
        onClose={() => (proformaResetting ? undefined : setProformaPreview(null))}
      />
    </Box>
  );
}
